/**
 * Gestionnaire d'export PDF avancé pour Maplytics
 */

class ExportManager {
    constructor(scopeSelector) {
        this.scopeSelector = scopeSelector;
    }

    notify(level, title, detail) {
        if (window.MaplyticsExportClient?.notify) {
            window.MaplyticsExportClient.notify(level, title, detail);
            return;
        }
        console[level === 'error' ? 'error' : 'warn'](`${title}: ${detail}`);
    }

    showLoginRequired(modal) {
        this.notify(
            'warning',
            'Connexion requise',
            'Connectez-vous pour utiliser cet export.',
        );
        const footer = modal?.querySelector('.export-modal-footer');
        if (!footer || footer.querySelector('[data-export-login]')) return;
        const loginLink = document.createElement('a');
        loginLink.className = 'btn-primary';
        loginLink.dataset.exportLogin = 'true';
        loginLink.textContent = 'Se connecter';
        const next = `${window.location.pathname}${window.location.search}`;
        loginLink.href = `/auth/login?next=${encodeURIComponent(next)}`;
        footer.appendChild(loginLink);
        loginLink.focus();
    }

    async readInlineLogo(file) {
        if (!file) return '';
        const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);
        if (!allowedTypes.has(file.type)) {
            throw new Error('Choisissez une image PNG, JPG ou WEBP.');
        }
        if (file.size > 1_000_000) {
            throw new Error('Le logo doit peser 1 Mo maximum.');
        }
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Le logo n’a pas pu être lu.'));
            reader.readAsDataURL(file);
        });
    }

    normalizeStatisticsData(snapshot) {
        const source = snapshot && typeof snapshot === 'object' ? snapshot : {};
        const nested = source.data && typeof source.data === 'object'
            ? source.data
            : {};
        const rawRows = Array.isArray(source.rows)
            ? source.rows
            : (Array.isArray(nested.rows) ? nested.rows : []);
        const rows = rawRows
            .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
            .map((row) => ({...row}));
        const numeric = (value) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };
        const sum = (keys) => rows.reduce(
            (total, row) => total + numeric(
                keys.map((key) => row[key]).find(
                    (value) => value !== undefined && value !== null,
                ),
            ),
            0,
        );
        const hasAny = (keys) => rows.some(
            (row) => keys.some((key) => row[key] !== undefined && row[key] !== null),
        );
        const normalized = {
            rows,
            selected_stat_type: source.statType || source.selected_stat_type || null,
            display_format: source.format || source.display_format || null,
            selected_zones: Array.isArray(source.selectedZones)
                ? [...source.selectedZones]
                : (Array.isArray(source.selected_zones) ? [...source.selected_zones] : []),
        };

        if (hasAny(['population', 'pop_totale'])) {
            normalized.population = {
                total: sum(['population', 'pop_totale']),
            };
        }
        if (hasAny(['menages'])) {
            normalized.menages = {total: sum(['menages'])};
        }

        const revenueValues = rows
            .map((row) => numeric(row.revenus ?? row.revenu_median ?? row.revenu_moyen))
            .filter((value) => value > 0)
            .sort((left, right) => left - right);
        if (revenueValues.length) {
            const middle = Math.floor(revenueValues.length / 2);
            const median = revenueValues.length % 2
                ? revenueValues[middle]
                : (revenueValues[middle - 1] + revenueValues[middle]) / 2;
            normalized.revenus = {
                median,
                mean: revenueValues.reduce((total, value) => total + value, 0)
                    / revenueValues.length,
            };
        }

        const ageGroups = [
            ['0-14 ans', 'pop_0_14'],
            ['15-19 ans', 'pop_15_19'],
            ['20-24 ans', 'pop_20_24'],
            ['25-39 ans', 'pop_25_39'],
            ['40-54 ans', 'pop_40_54'],
            ['55-64 ans', 'pop_55_64'],
            ['65-79 ans', 'pop_65_79'],
            ['80 ans et +', 'pop_80_plus'],
        ].map(([label, key]) => ({label, count: sum([key])}));
        const ageTotal = ageGroups.reduce((total, group) => total + group.count, 0);
        if (ageTotal > 0) {
            normalized.age = ageGroups.map((group) => ({
                ...group,
                percent: Number(((group.count / ageTotal) * 100).toFixed(1)),
            }));
        }

        const cspGroups = [
            ['Cadres', 'csp_cadres'],
            ['Professions intermédiaires', 'csp_prof_inter'],
            ['Employés', 'csp_employes'],
            ['Ouvriers', 'csp_ouvriers'],
            ['Retraités', 'csp_retraites'],
            ['Artisans', 'csp_artisans'],
            ['Agriculteurs', 'csp_agriculteurs'],
        ].map(([label, key]) => ({label, count: sum([key])}));
        const cspTotal = cspGroups.reduce((total, group) => total + group.count, 0);
        if (cspTotal > 0) {
            normalized.csp = cspGroups
                .filter((group) => group.count > 0)
                .map((group) => ({
                    ...group,
                    percent: Number(((group.count / cspTotal) * 100).toFixed(1)),
                }));
        }

        [
            'competition',
            'concurrence',
            'accessibility',
            'potential',
            'housing',
            'competitors',
            'map',
            'recommendations',
        ].forEach((key) => {
            const value = source[key] ?? nested[key];
            if (value !== undefined && value !== null) normalized[key] = value;
        });
        return normalized;
    }

    hasExportableStatistics(statisticsData) {
        if (statisticsData.rows?.length) return true;
        return Object.keys(statisticsData).some(
            (key) => !['rows', 'selected_stat_type', 'display_format', 'selected_zones']
                .includes(key),
        );
    }
    
    showExportModal(statisticsData) {
        const existingModal = document.querySelector('.export-modal');
        if (existingModal?.closeExportModal) {
            existingModal.closeExportModal();
        } else {
            existingModal?.remove();
        }
        const trigger = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const modal = document.createElement('div');
        modal.className = 'export-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', 'exportModalTitle');
        const exportManagerInstance = this; // Capturer l'instance pour l'utiliser dans les event listeners
        const canCreateSummary = window.maplyticsHasFeature?.('advanced_analytics') === true;
        const canUseDemographics = window.maplyticsHasFeature?.('demographic_data') === true;
        const formatOptions = [
            '<option value="standard">PDF avancé</option>',
            canCreateSummary
                ? '<option value="executive_summary">Synthèse exécutive</option>'
                : '',
        ].join('');
        modal.innerHTML = `
            <div class="export-modal-content">
                <div class="export-modal-header">
                    <h2 id="exportModalTitle">Configuration de l'export PDF</h2>
                    <button type="button" class="export-modal-close" id="exportModalCloseBtn" aria-label="Fermer">×</button>
                </div>
                <div class="export-modal-body">
                    <div class="export-section">
                        <h3>Sections à inclure</h3>
                        <div class="export-checkboxes" id="exportSections">
                            <label><input type="checkbox" value="summary" checked> Résumé</label>
                            <label><input type="checkbox" value="rows" checked> Données affichées</label>
                            ${canUseDemographics ? `
                                <label><input type="checkbox" value="population" checked> Population</label>
                                <label><input type="checkbox" value="csp" checked> CSP</label>
                                <label><input type="checkbox" value="revenus" checked> Revenus</label>
                                <label><input type="checkbox" value="age" checked> Répartition par âge</label>
                            ` : ''}
                        </div>
                    </div>
                    <div class="export-section">
                        <h3>Format</h3>
                        <select id="exportFormat">
                            ${formatOptions}
                        </select>
                    </div>
                    <div class="export-section">
                        <h3>Personnalisation</h3>
                        <label>
                            Logo
                            <input type="file" id="exportLogoFile" accept="image/png,image/jpeg,image/webp">
                        </label>
                        <p class="export-field-help">PNG, JPG ou WEBP, 1 Mo maximum.</p>
                    </div>
                </div>
                <div class="export-modal-footer">
                    <button type="button" class="btn-secondary" id="exportModalCancelBtn">Annuler</button>
                    <button type="button" class="btn-primary" id="exportModalExportBtn">Exporter</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Stocker les données pour l'export
        modal.statisticsData = this.normalizeStatisticsData(statisticsData);
        
        // Event listeners pour les boutons du modal
        const closeBtn = modal.querySelector('#exportModalCloseBtn');
        const cancelBtn = modal.querySelector('#exportModalCancelBtn');
        const exportBtn = modal.querySelector('#exportModalExportBtn');
        
        const closeModal = () => {
            modal.removeEventListener('keydown', handleModalKeydown);
            modal.remove();
            if (trigger?.isConnected) trigger.focus();
        };
        const focusableSelector = [
            'button:not([disabled])',
            'select:not([disabled])',
            'input:not([disabled])',
            'a[href]',
        ].join(',');
        const handleModalKeydown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModal();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(modal.querySelectorAll(focusableSelector))
                .filter((element) => element.getClientRects().length > 0);
            if (!focusable.length) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        modal.closeExportModal = closeModal;
        modal.addEventListener('keydown', handleModalKeydown);
        
        if (closeBtn) closeBtn.addEventListener('click', closeModal);
        if (cancelBtn) cancelBtn.addEventListener('click', closeModal);
        if (exportBtn) {
            exportBtn.addEventListener('click', async () => {
                const result = await exportManagerInstance.exportGroupedPDF();
                // Le modal sera fermé dans exportGroupedPDF() si l'export réussit
                // Sinon, on le ferme manuellement en cas d'erreur
                if (!result) {
                    // Ne pas fermer le modal en cas d'erreur pour que l'utilisateur puisse réessayer
                    console.warn('Export échoué, modal reste ouvert');
                }
            });
        }
        window.requestAnimationFrame(() => closeBtn?.focus());
    }
    
    async exportGroupedPDF() {
        const modal = document.querySelector('.export-modal');
        if (!modal) {
            console.error('Modal d\'export introuvable');
            this.notify(
                'error',
                'Export PDF indisponible',
                'La fenêtre d’export n’est plus disponible. Rouvrez-la.',
            );
            return null;
        }
        
        // Récupérer les données statistiques
        const statisticsData = modal.statisticsData;
        if (
            !statisticsData
            || typeof statisticsData !== 'object'
            || !this.hasExportableStatistics(statisticsData)
        ) {
            this.notify(
                'warning',
                'Aucune donnée à exporter',
                'Générez d’abord les statistiques à inclure.',
            );
            return null;
        }
        
        // Récupérer les sections sélectionnées
        const sectionCheckboxes = modal.querySelectorAll('#exportSections input:checked');
        const sections = Array.from(sectionCheckboxes).map(cb => cb.value);
        
        if (sections.length === 0) {
            this.notify(
                'warning',
                'Sélection requise',
                'Sélectionnez au moins une section.',
            );
            return null;
        }
        
        const formatSelect = modal.querySelector('#exportFormat');
        const format = formatSelect ? formatSelect.value : 'standard';
        
        const logoFileInput = modal.querySelector('#exportLogoFile');
        
        const exportConfig = {
            sections: sections,
            order: sections,
            format: format
        };
        
        const userConfig = {};
        
        // Afficher un indicateur de chargement
        const exportBtn = modal.querySelector('#exportModalExportBtn') || modal.querySelector('.btn-primary');
        const originalBtnText = exportBtn ? exportBtn.textContent : '';
        if (exportBtn) {
            exportBtn.disabled = true;
            exportBtn.textContent = 'Génération en cours...';
        }
        
        try {
            const logoFile = logoFileInput?.files?.[0];
            if (logoFile) {
                userConfig.logo_data_uri = await this.readInlineLogo(logoFile);
            }
            const result = await window.MaplyticsExportClient.postJson(
                `grouped-pdf:${format}`,
                '/api/export/grouped-pdf',
                {
                    async_generation: true,
                    statistics_data: statisticsData,
                    export_config: exportConfig,
                    user_config: userConfig
                },
            );

            if (result?.success && (result.download_url || result.job_id)) {
                modal.closeExportModal?.();
                return result;
            } else {
                throw new Error(result.detail || result.error || 'Erreur lors de l\'export');
            }
        } catch (error) {
            // Ne pas logger d'erreur pour les 401
            if (error.status === 401) {
                this.showLoginRequired(modal);
                return null;
            }
            if (error.message && !error.message.includes('401')) {
                console.error('Erreur lors de l\'export PDF:', error);
                this.notify(
                    'error',
                    'Export PDF indisponible',
                    error.message || 'Veuillez réessayer.',
                );
            }
            // Ne pas re-throw pour éviter de planter
            return null;
        } finally {
            // Restaurer le bouton
            if (exportBtn) {
                exportBtn.disabled = false;
                exportBtn.textContent = originalBtnText;
            }
        }
    }

    
    async generateExecutiveSummary(statisticsData) {
        try {
            if (!window.MaplyticsExportClient?.postJsonData) {
                throw new Error('Le service de synthèse n’est pas encore disponible.');
            }
            const result = await window.MaplyticsExportClient.postJsonData(
                'executive-summary',
                '/api/export/executive-summary',
                {
                    statistics_data: statisticsData,
                    generate_pdf: false
                },
            );
            if (result.success) {
                return result.summary;
            }
            throw new Error(result.detail || 'Erreur lors de la génération');
        } catch (error) {
            if (error.status === 401) {
                this.showLoginRequired(document.querySelector('.export-modal'));
                return null;
            }
            if (error.message && !error.message.includes('401')) {
                console.error('Erreur lors de la génération du résumé:', error);
            }
            throw error;
        }
    }
    
    async exportBankDossier(statisticsData, projections) {
        try {
            const result = await window.MaplyticsExportClient.postJson(
                'grouped-pdf:bank-dossier',
                '/api/export/grouped-pdf',
                {
                    async_generation: true,
                    statistics_data: statisticsData,
                    export_config: {
                        format: 'bank_dossier',
                        sections: ['summary'],
                    },
                    projections: projections || {}
                }
            );

            if (result?.success && (result.download_url || result.job_id)) {
                return result;
            } else {
                throw new Error(result.detail || 'Erreur lors de l\'export');
            }
        } catch (error) {
            if (error.status === 401) {
                this.showLoginRequired(document.querySelector('.export-modal'));
                return null;
            }
            // Ne pas logger d'erreur pour les 401
            if (error.message && !error.message.includes('401')) {
                console.error('Erreur lors de l\'export dossier bancaire:', error);
            }
            throw error;
        }
    }
}
